/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-08-08 14:11:26
 */
import React, { useMemo, useState, useCallback } from 'react'
import { Button, Typography, App } from 'antd'
import { useAIRecognition } from '../context/AIRecognitionContext'
import { XCSelectionInfo } from '../types'
import '../index.scss'
import './RecognitionContentWaitingV2.scss'
import selectionIconTrue from '../assets/selection-icon-true.png'
import selectionIconFalse from '../assets/selection-icon-false.png'

const { Text } = Typography

/**
 * 框选区域组件
 */
interface XCSelectionAreaProps {
  hasSelection: boolean
}

const SelectionArea: React.FC<XCSelectionAreaProps> = React.memo(
  ({ hasSelection }) => (
    <div className="selection-area">
      <div className="selection-box">
        <div className="selection-icon">
          <img
            src={hasSelection ? selectionIconTrue : selectionIconFalse}
            className="selection-icon"
          />
        </div>
        <Text className="selection-status">
          {hasSelection ? '已框选' : '未框选'}
        </Text>
      </div>
    </div>
  )
)

/**
 * 模型选择组件 - 已选定状态
 */
interface XCSelectedModelProps {
  scenario: any
  onSwitch: () => void
}

const SelectedModel: React.FC<XCSelectedModelProps> = React.memo(
  ({ scenario, onSwitch }) => (
    <>
      <div className="recognition-tip">
        <Text className="tip-text">
          识别到你框选的安全隐患部位是「{scenario?.name || ''}」，推荐你使用
        </Text>
      </div>
      <div className="model-description">
        <Text className="description-text">{scenario?.description || ''}</Text>
        <Button className="switch-button" type="link" onClick={onSwitch}>
          切换
        </Button>
      </div>
      <div className="model-name">
        <Text className="model-name-text">
          {scenario?.name || '脚手架模型'}
        </Text>
      </div>
    </>
  )
)

/**
 * 模型选择组件 - 未选定状态
 */
interface XCUnselectedModelProps {
  onSelect: () => void
}

const UnselectedModel: React.FC<XCUnselectedModelProps> = React.memo(
  ({ onSelect }) => (
    <div className="model-placeholder">
      <Text className="placeholder-text">请选择模型</Text>
      <Button className="switch-button" type="link" onClick={onSelect}>
        去选择
      </Button>
    </div>
  )
)

/**
 * 等待识别组件 Props 接口
 */
interface XCRecognitionContentWaitingProps {
  onShowSafetyScenarioSelection?: (selectionInfo: XCSelectionInfo[]) => void
}

/**
 * 等待识别组件 - 根据Figma设计稿实现
 */
const RecognitionContentWaitingV2: React.FC<XCRecognitionContentWaitingProps> =
  React.memo(({ onShowSafetyScenarioSelection }) => {
    const { message } = App.useApp()
    const {
      startRecognition,
      selectedSafetyScenarios,
      selectedImages,
      selectionInfo,
      imageSelectionEditorRef
    } = useAIRecognition()

    const [selectedModel, setSelectedModel] = useState<string | undefined>(
      undefined
    )

    // 判断是否已选定模型
    const hasSelectedModel = selectedSafetyScenarios.length > 0
    const selectedScenario = hasSelectedModel
      ? selectedSafetyScenarios[0]
      : null

    // 判断是否有选择图片
    const hasSelectedImages = selectedImages.length > 0

    // 判断是否有框选区域
    const hasSelection = Boolean(selectionInfo?.length)

    /**
     * 验证并处理开始识别
     */
    const handleStartRecognition = useCallback(() => {
      // 验证是否选择了图片
      if (!hasSelectedImages) {
        message.warning('请选择图片')
        return
      }

      // 验证是否框选了区域
      if (!hasSelection) {
        message.warning('请先框选要识别的区域')
        return
      }

      // 验证是否选择了安全隐患模型
      if (!hasSelectedModel) {
        message.warning('请选择安全隐患模型')
        return
      }

      startRecognition()
    }, [hasSelectedImages, hasSelection, hasSelectedModel, startRecognition])

    /**
     * 处理切换模型
     */
    const handleSwitchModel = useCallback(() => {
      // 验证是否选择了图片
      if (!hasSelectedImages) {
        message.warning('请选择图片')
        return
      }

      // 验证是否框选了区域
      if (!hasSelection) {
        message.warning('请先框选要识别的区域')
        return
      }

      // 触发安全场景选择
      onShowSafetyScenarioSelection &&
        onShowSafetyScenarioSelection(selectionInfo || [])
    }, [
      hasSelectedImages,
      hasSelection,
      onShowSafetyScenarioSelection,
      selectionInfo
    ])

    /**
     * 处理选择模型
     */
    const handleSelectModel = useCallback(() => {
      // 验证是否选择了图片
      if (!hasSelectedImages) {
        message.warning('请选择图片')
        return
      }

      // 验证是否框选了区域
      if (!hasSelection) {
        message.warning('请先框选要识别的区域')
        // 触发动画以引起用户注意
        if (imageSelectionEditorRef?.current) {
          imageSelectionEditorRef.current.triggerAnimation()
        }
        return
      }

      // 触发安全场景选择
      onShowSafetyScenarioSelection &&
        onShowSafetyScenarioSelection(selectionInfo || [])
    }, [
      hasSelectedImages,
      hasSelection,
      onShowSafetyScenarioSelection,
      imageSelectionEditorRef,
      selectionInfo
    ])

    /**
     * 渲染主要内容
     */
    const renderContent = useMemo(
      () => (
        <div className="waiting-recognition">
          <div className="steps-container">
            {/* 步骤1：框选区域 */}
            <div className="step-section">
              <div className="step-title">
                <Text className="step-title-text">1.请框选要识别的区域</Text>
              </div>
              <SelectionArea hasSelection={hasSelection} />
            </div>

            {/* 步骤2：选择模型 */}
            <div className="step-section">
              <div className="step-title">
                <Text className="step-title-text">2.选择安全隐患模型</Text>
              </div>
              {hasSelectedModel ? (
                <SelectedModel
                  scenario={selectedScenario}
                  onSwitch={handleSwitchModel}
                />
              ) : (
                <UnselectedModel onSelect={handleSelectModel} />
              )}
            </div>

            {/* 去识别按钮 */}
            <div className="recognition-button">
              <Button className="start-button" onClick={handleStartRecognition}>
                去识别
              </Button>
            </div>
          </div>
        </div>
      ),
      [
        hasSelection,
        hasSelectedModel,
        selectedScenario,
        handleSwitchModel,
        handleSelectModel,
        handleStartRecognition
      ]
    )

    return renderContent
  })

RecognitionContentWaitingV2.displayName = 'RecognitionContentWaitingV2'

export default RecognitionContentWaitingV2
